import React from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { Button } from './ui/button';

export const CalendarView = ({ 
  tasks, 
  currentMonth, 
  onDayClick, 
  onNavigateMonth,
  getDaysInMonth,
  getTasksByDate 
}) => {
  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const allDays = React.useMemo(() => {
    const days = [];
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayTasks = getTasksByDate(date);
      const isToday = new Date().toDateString() === date.toDateString();
      days.push({ day, date, dayTasks, isToday });
    }
    return days;
  }, [daysInMonth, year, month, getTasksByDate]);

  const renderDesktopGrid = () => {
    const cells = [];
    
    for (let i = 0; i < startingDayOfWeek; i++) {
      cells.push(
        <div key={`empty-${i}`} className="h-28 bg-slate-50 border border-slate-200"></div>
      );
    }
    
    for (const { day, date, dayTasks, isToday } of allDays) {
      cells.push(
        <div
          key={day}
          className={`h-28 border border-slate-200 p-2 hover:bg-slate-50 transition-colors cursor-pointer ${
            isToday ? 'bg-blue-50 border-blue-300' : 'bg-white'
          }`}
          onClick={() => onDayClick(dayTasks)}
          data-testid={`calendar-day-${day}`}
        >
          <div className="flex justify-between items-start mb-1">
            <span className={`text-sm font-semibold ${isToday ? 'text-blue-600' : 'text-slate-700'}`}>
              {day}
            </span>
            {dayTasks.length > 0 && (
              <span className="text-xs bg-blue-600 text-white rounded-full px-1.5 py-0.5 font-medium">
                {dayTasks.length}
              </span>
            )}
          </div>
          <div className="space-y-1 overflow-hidden">
            {dayTasks.slice(0, 2).map((task) => (
              <div 
                key={`${day}-${task.id}`}
                className={`text-xs p-1 rounded truncate ${
                  task.status === 'open'
                    ? 'bg-blue-100 text-blue-800 border border-blue-200'
                    : 'bg-green-100 text-green-800 border border-green-200'
                }`}
                title={task.activity}
              >
                {task.activity}
              </div>
            ))}
            {dayTasks.length > 2 && (
              <div className="text-xs text-slate-500 pl-1">
                +{dayTasks.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    }
    
    return cells;
  };

  const daysWithTasks = allDays.filter(d => d.dayTasks.length > 0);

  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg sm:text-xl font-semibold text-slate-900">
          {monthNames[month]} {year}
        </h3>
        <div className="flex gap-1 sm:gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigateMonth(-1)}
            data-testid="prev-month"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigateMonth(0)}
            data-testid="today"
          >
            Today
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onNavigateMonth(1)}
            data-testid="next-month"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Desktop: 7-col grid */}
      <div className="hidden sm:block">
        <div className="grid grid-cols-7 gap-0">
          {dayNames.map((dayName) => (
            <div
              key={dayName}
              className="h-10 border border-slate-200 bg-slate-100 flex items-center justify-center font-semibold text-sm text-slate-700"
            >
              {dayName}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0">
          {renderDesktopGrid()}
        </div>
      </div>

      {/* Mobile: vertical list of days with tasks */}
      <div className="block sm:hidden space-y-2">
        {/* Mini date strip */}
        <div className="grid grid-cols-7 gap-0">
          {dayNames.map((dayName) => (
            <div key={dayName} className="text-center text-[10px] font-semibold text-slate-500 uppercase py-1">
              {dayName.charAt(0)}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-0">
          {Array.from({ length: startingDayOfWeek }, (_, i) => (
            <div key={`me-${i}`} className="h-8"></div>
          ))}
          {allDays.map(({ day, dayTasks, isToday }) => (
            <div
              key={day}
              className={`h-8 flex items-center justify-center text-xs font-medium rounded-full mx-auto w-8 cursor-pointer transition-colors ${
                isToday ? 'bg-blue-600 text-white' : dayTasks.length > 0 ? 'bg-blue-100 text-blue-700 font-bold' : 'text-slate-600'
              }`}
              onClick={() => dayTasks.length > 0 && onDayClick(dayTasks)}
              data-testid={`calendar-day-mobile-${day}`}
            >
              {day}
            </div>
          ))}
        </div>

        {/* Task cards for days with tasks */}
        <div className="mt-3 space-y-3">
          {daysWithTasks.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">No tasks this month</div>
          ) : (
            daysWithTasks.map(({ day, date, dayTasks, isToday }) => (
              <div
                key={day}
                className={`border rounded-lg overflow-hidden ${isToday ? 'border-blue-400 bg-blue-50/50' : 'border-slate-200 bg-white'}`}
                data-testid={`calendar-mobile-card-${day}`}
              >
                <div className={`px-3 py-2 flex items-center justify-between ${isToday ? 'bg-blue-100' : 'bg-slate-50'} border-b border-slate-200`}>
                  <span className={`text-sm font-bold ${isToday ? 'text-blue-700' : 'text-slate-800'}`}>
                    {dayNames[date.getDay()]} {day}
                  </span>
                  <span className="text-xs bg-blue-600 text-white rounded-full px-2 py-0.5 font-medium">
                    {dayTasks.length} task{dayTasks.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="p-2 space-y-2">
                  {dayTasks.map((task) => (
                    <div
                      key={task.id}
                      className={`p-2.5 rounded-lg border ${
                        task.status === 'open'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-green-50 border-green-200'
                      }`}
                    >
                      <div className="font-semibold text-sm text-slate-900 mb-1">{task.activity}</div>
                      {task.content && (
                        <div className="text-xs text-slate-600 mb-2 line-clamp-2">{task.content}</div>
                      )}
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1 text-slate-500">
                          <Clock className="w-3 h-3" />
                          {task.hours_log || 0}h
                        </div>
                        <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                          task.status === 'open' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs sm:text-sm text-slate-600 pt-4 border-t border-slate-200">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-blue-100 border border-blue-200"></div>
          <span>Open Tasks</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-green-100 border border-green-200"></div>
          <span>Complete Tasks</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 sm:w-4 sm:h-4 rounded bg-blue-50 border border-blue-300"></div>
          <span>Today</span>
        </div>
      </div>
    </div>
  );
};
